import { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchablePicker, { productMatch, renderProductRow } from "@/components/app/SearchablePicker";

export default function PriceHistory() {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [productId, setProductId] = useState("");
  const [brandId, setBrandId] = useState("all");
  const [vendorId, setVendorId] = useState("all");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/purchasing/products"),
      api.get("/purchasing/brands"),
      api.get("/purchasing/vendors"),
    ]).then(([p, b, v]) => { setProducts(p.data); setBrands(b.data); setVendors(v.data); });
  }, []);

  useEffect(() => {
    const q = new URLSearchParams();
    if (productId) q.set("product_id", productId);
    if (brandId !== "all") q.set("brand_id", brandId);
    if (vendorId !== "all") q.set("vendor_id", vendorId);
    api.get(`/purchasing/price-history?${q.toString()}`).then((r) => setRows(r.data));
  }, [productId, brandId, vendorId]);

  const brandName = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b.name])), [brands]);
  const vendorName = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.name])), [vendors]);
  const brandsForProduct = brands.filter((b) => b.product_id === productId);

  const prices = rows.map((r) => Number(r.price) || 0);
  const stats = prices.length ? {
    latest: rows[0]?.price,
    lowest: Math.min(...prices),
    highest: Math.max(...prices),
    avg: prices.reduce((a, b) => a + b, 0) / prices.length,
  } : null;

  return (
    <div data-testid="pv-price-history-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Vendor Price History"
        subtitle="Unified timeline of enquiries and actual purchases. Never lose historical pricing."
      />
      <div className="p-4 sm:p-8">
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="w-64">
            <SearchablePicker
              items={products}
              value={productId}
              onChange={(v) => { setProductId(v); setBrandId("all"); }}
              placeholder="Pick a product"
              testid="pv-ph-product"
              selectedLabel={(p) => p.name}
              matchItem={productMatch}
              renderRow={renderProductRow}
            />
          </div>
          <Select value={brandId} onValueChange={setBrandId}>
            <SelectTrigger className="rounded-none h-10 w-48" data-testid="pv-ph-brand"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brands</SelectItem>
              {brandsForProduct.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vendorId} onValueChange={setVendorId}>
            <SelectTrigger className="rounded-none h-10 w-56" data-testid="pv-ph-vendor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All vendors</SelectItem>
              {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="pv-ph-stats">
            <Stat label="Latest" value={inr(stats.latest)} />
            <Stat label="Lowest" value={inr(stats.lowest)} color="#10B981" />
            <Stat label="Highest" value={inr(stats.highest)} color="#DC2626" />
            <Stat label="Average" value={inr(Math.round(stats.avg * 100) / 100)} />
          </div>
        )}

        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Type", "Date", "Vendor", "Brand", "Price", "Delivery", "Remarks"].map((h, i) => (
                <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pv-ph-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 uppercase tracking-widest font-bold text-[9px] ${r.type === "po" ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>
                      {r.type === "po" ? "PO" : "Quote"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{r.date}</td>
                  <td className="px-4 py-3 text-xs font-semibold">{vendorName[r.vendor_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs">{brandName[r.brand_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono">{inr(r.price)}</td>
                  <td className="px-4 py-3 text-xs">{r.delivery_time_days ? `${r.delivery_time_days} d` : "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]">{r.remarks || "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center text-sm text-[#9CA3AF] py-12">
                {productId ? "No history for this filter yet." : "Pick a product to view its price history."}
              </td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-[#E5E7EB] p-4">
      <div className="label-eyebrow">{label}</div>
      <div className="text-xl font-black tracking-tight mt-1 font-mono" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}
